import { useEffect, useMemo, useState } from "react";
import {
  Database,
  Edit3,
  KeyRound,
  LogOut,
  Plus,
  Save,
  Settings,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui";
import {
  accumulators,
  bookmakers,
  countries,
  matches,
  promotions,
  tips,
} from "../data/demo";
import { config } from "../config";

type ResourceName =
  | "Matches"
  | "Teams"
  | "Bookmakers"
  | "Countries"
  | "Odds"
  | "Tips"
  | "Accumulators"
  | "Promotions"
  | "Affiliate links"
  | "Site settings";
type ApiRecord = Record<string, string | number | boolean | null>;
type AdminRow = {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  raw?: ApiRecord;
  isNew?: boolean;
};

const resources: ResourceName[] = [
  "Matches",
  "Teams",
  "Bookmakers",
  "Countries",
  "Odds",
  "Tips",
  "Accumulators",
  "Promotions",
  "Affiliate links",
  "Site settings",
];
const baseRows: Record<ResourceName, AdminRow[]> = {
  Matches: matches.map((item) => ({
    id: item.id,
    title: `${item.home.name} vs ${item.away.name}`,
    subtitle: item.competition,
    status: item.status,
  })),
  Teams: [
    ...new Map(
      matches
        .flatMap((item) => [item.home, item.away])
        .map((item) => [item.id, item]),
    ).values(),
  ].map((item) => ({
    id: item.id,
    title: item.name,
    subtitle: item.short,
    status: "active",
  })),
  Bookmakers: bookmakers.map((item) => ({
    id: item.id,
    title: item.name,
    subtitle: `${item.rating}/10 · ${item.countries.length} countries`,
    status: "published",
  })),
  Countries: countries.map((item) => ({
    id: item.code,
    title: `${item.flag} ${item.name}`,
    subtitle: item.currency,
    status: "active",
  })),
  Odds: matches.map((item) => ({
    id: `odds-${item.id}`,
    title: `${item.home.short} vs ${item.away.short}`,
    subtitle: "3 bookmaker rows · demo",
    status: "sample",
  })),
  Tips: tips.map((item) => ({
    id: item.id,
    title: item.selection,
    subtitle: `${item.market} · ${item.oddsAtPublication.toFixed(2)}`,
    status: item.status,
  })),
  Accumulators: accumulators.map((item) => ({
    id: item.id,
    title: item.title,
    subtitle: `${item.selections.length} selections`,
    status: "published",
  })),
  Promotions: promotions.map((item) => ({
    id: item.id,
    title: item.title,
    subtitle: `${item.countries.length} countries`,
    status: "published",
  })),
  "Affiliate links": bookmakers.flatMap((item) =>
    Object.entries(item.affiliateUrls).map(([country, url]) => ({
      id: `${item.id}-${country}`,
      title: `${item.name} · ${country.toUpperCase()}`,
      subtitle: url,
      status: "active",
    })),
  ),
  "Site settings": [
    {
      id: "brand",
      title: "Brand & SEO defaults",
      subtitle: "Site name, canonical URL, default metadata",
      status: "configured",
    },
    {
      id: "responsible",
      title: "Responsible gambling",
      subtitle: "Global and country-specific messages",
      status: "configured",
    },
    {
      id: "features",
      title: "Feature flags",
      subtitle: "Premium disabled · Accounts disabled",
      status: "configured",
    },
  ],
};

export function AdminPage() {
  const [token, setToken] = useState("");
  const [authenticated, setAuthenticated] = useState(
    () => sessionStorage.getItem("admin-demo") === "true",
  );
  const [apiMode, setApiMode] = useState(false);
  const [active, setActive] = useState<ResourceName>("Matches");
  const [rows, setRows] = useState(baseRows);
  const [editing, setEditing] = useState<AdminRow | null>(null);
  const [rawJson, setRawJson] = useState("{}");
  const [notice, setNotice] = useState("");
  const currentRows = useMemo(() => rows[active], [rows, active]);
  const resourceKey = (resource: ResourceName) =>
    ({
      Matches: "matches",
      Teams: "teams",
      Bookmakers: "bookmakers",
      Countries: "countries",
      Odds: "odds",
      Tips: "tips",
      Accumulators: "accumulators",
      Promotions: "promotions",
      "Affiliate links": "affiliates",
      "Site settings": "settings",
    })[resource];
  const login = async () => {
    if (token.trim().length < 12) {
      setNotice("Use at least 12 characters for the local demo token.");
      return;
    }
    try {
      const response = await fetch(`${config.apiUrl}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("API token was not accepted");
      setApiMode(true);
      setAuthenticated(true);
      setNotice("Connected to the protected D1 admin API.");
    } catch {
      if (!config.demoMode) {
        setNotice("The admin API could not verify this token.");
        return;
      }
      setApiMode(false);
      setAuthenticated(true);
      setNotice("API unavailable, so the session-only demo editor is active.");
    }
    sessionStorage.setItem("admin-demo", "true");
  };
  useEffect(() => {
    if (!apiMode || !authenticated) return;
    const load = async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}/api/admin/${resourceKey(active)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) throw new Error("Could not load records");
        const payload = (await response.json()) as { data: ApiRecord[] };
        const mapped = payload.data.map((record) => ({
          id: String(record.id),
          title: String(
            record.name ||
              record.title ||
              record.selection ||
              record.slug ||
              record.id,
          ),
          subtitle: String(
            record.description ||
              record.market ||
              record.currency ||
              record.status ||
              "D1 record",
          ),
          status: String(
            record.status || (record.active ? "active" : "stored"),
          ),
          raw: record,
        }));
        setRows((current) => ({ ...current, [active]: mapped }));
      } catch {
        setNotice(`Could not load ${active} from the API.`);
      }
    };
    void load();
  }, [active, apiMode, authenticated, token]);
  const save = async () => {
    if (!editing || (!apiMode && !editing.title.trim())) return;
    if (apiMode) {
      try {
        const data = JSON.parse(rawJson) as ApiRecord;
        const response = await fetch(
          `${config.apiUrl}/api/admin/${resourceKey(active)}`,
          {
            method: editing.isNew ? "POST" : "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ id: editing.id, data }),
          },
        );
        if (!response.ok) throw new Error("Save failed");
        setNotice(`${active} saved to D1.`);
        setRows((current) => ({
          ...current,
          [active]: current[active].some((item) => item.id === editing.id)
            ? current[active].map((item) =>
                item.id === editing.id
                  ? { ...editing, raw: data, isNew: false }
                  : item,
              )
            : [{ ...editing, raw: data, isNew: false }, ...current[active]],
        }));
      } catch (error) {
        setNotice(
          error instanceof SyntaxError
            ? "The JSON fields are not valid."
            : "The API could not save this record. Check required fields.",
        );
        return;
      }
    } else {
      setRows((current) => ({
        ...current,
        [active]: current[active].some((item) => item.id === editing.id)
          ? current[active].map((item) =>
              item.id === editing.id ? editing : item,
            )
          : [editing, ...current[active]],
      }));
      setNotice(`${active} saved in this local demo session.`);
    }
    setEditing(null);
  };
  const remove = async (id: string) => {
    if (apiMode) {
      const response = await fetch(
        `${config.apiUrl}/api/admin/${resourceKey(active)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id, data: {} }),
        },
      );
      if (!response.ok) {
        setNotice("The API could not delete this record.");
        return;
      }
    }
    setRows((current) => ({
      ...current,
      [active]: current[active].filter((item) => item.id !== id),
    }));
    setNotice(
      apiMode
        ? `Record deleted from D1 ${active}.`
        : `Demo record removed from ${active}.`,
    );
  };
  if (!authenticated)
    return (
      <div className="page-shell grid min-h-[70vh] place-items-center py-10">
        <SEO
          title="Admin sign in"
          description="Protected content administration."
          path="/admin"
          noIndex
        />
        <section className="surface w-full max-w-md rounded-3xl p-7">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-lime/10 text-lime">
            <KeyRound />
          </span>
          <h1 className="display mt-5 text-3xl font-black">Admin access</h1>
          <p className="mt-3 text-sm leading-6 text-muted">
            For local demo use, enter any 12+ character token. Production API
            writes require the secret <code>ADMIN_API_TOKEN</code> configured in
            Cloudflare.
          </p>
          <label className="mt-6 block text-xs font-bold uppercase tracking-wider text-muted">
            Admin token
            <input
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") login();
              }}
              className="mt-2 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-white outline-none focus:border-lime/50"
            />
          </label>
          {notice && <p className="mt-3 text-xs text-amber-200">{notice}</p>}
          <Button className="mt-5 w-full" onClick={login}>
            <ShieldCheck size={17} />
            Unlock dashboard
          </Button>
        </section>
      </div>
    );
  return (
    <div className="page-shell py-8 sm:py-12">
      <SEO
        title="Admin dashboard"
        description="Manage platform content."
        path="/admin"
        noIndex
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[.2em] text-lime">
            Protected workspace
          </div>
          <h1 className="display mt-1 text-3xl font-black">
            Content dashboard
          </h1>
        </div>
        <button
          onClick={() => {
            sessionStorage.removeItem("admin-demo");
            setAuthenticated(false);
            setApiMode(false);
            setToken("");
          }}
          className="flex items-center gap-2 rounded-xl border border-soft px-4 py-2.5 text-sm font-bold text-muted"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
      <div className="mt-7 grid gap-6 lg:grid-cols-[230px_1fr]">
        <aside className="surface h-fit rounded-2xl p-3">
          <div className="mb-2 flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted">
            <Database size={15} />
            Content
          </div>
          {resources.map((resource) => (
            <button
              key={resource}
              onClick={() => {
                setActive(resource);
                setEditing(null);
              }}
              className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold ${active === resource ? "bg-lime text-[#07110d]" : "text-muted hover:bg-white/[.04] hover:text-white"}`}
            >
              {resource === "Site settings" && <Settings size={15} />}
              {resource}
            </button>
          ))}
        </aside>
        <section>
          <div className="surface rounded-2xl">
            <div className="flex items-center justify-between border-b border-soft p-5">
              <div>
                <h2 className="display text-xl font-black">{active}</h2>
                <p className="mt-1 text-xs text-muted">
                  {currentRows.length} {apiMode ? "D1" : "demo"} records
                </p>
              </div>
              <Button
                onClick={() => {
                  const next = {
                    id: crypto.randomUUID(),
                    title: "",
                    subtitle: "",
                    status: "draft",
                    isNew: true,
                  };
                  setEditing(next);
                  setRawJson("{}");
                }}
              >
                <Plus size={16} />
                Add
              </Button>
            </div>
            {notice && (
              <div className="border-b border-soft bg-lime/5 px-5 py-3 text-xs text-lime">
                {notice}
              </div>
            )}
            <div className="divide-y divide-white/10">
              {currentRows.map((row) => (
                <div key={row.id} className="flex items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-bold">{row.title}</div>
                    <div className="mt-1 truncate text-xs text-muted">
                      {row.subtitle}
                    </div>
                  </div>
                  <span className="hidden rounded-full bg-white/[.05] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-muted sm:block">
                    {row.status}
                  </span>
                  <button
                    aria-label={`Edit ${row.title}`}
                    onClick={() => {
                      setEditing(row);
                      setRawJson(
                        JSON.stringify(
                          Object.fromEntries(
                            Object.entries(row.raw || {}).filter(
                              ([key]) => key !== "id",
                            ),
                          ),
                          null,
                          2,
                        ),
                      );
                    }}
                    className="grid h-9 w-9 place-items-center rounded-lg border border-soft text-muted hover:text-white"
                  >
                    <Edit3 size={15} />
                  </button>
                  <button
                    aria-label={`Delete ${row.title}`}
                    onClick={() => {
                      void remove(row.id);
                    }}
                    className="grid h-9 w-9 place-items-center rounded-lg border border-rose-300/15 text-rose-300"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          {editing && (
            <div className="surface mt-4 rounded-2xl p-5">
              <h3 className="display text-lg font-black">Edit {active}</h3>
              {apiMode ? (
                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-muted">
                  Allowed D1 fields (JSON)
                  <textarea
                    value={rawJson}
                    onChange={(event) => setRawJson(event.target.value)}
                    className="mt-2 min-h-72 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 font-mono text-xs leading-6 text-white outline-none"
                  />
                </label>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted">
                    Title
                    <input
                      value={editing.title}
                      onChange={(event) =>
                        setEditing({ ...editing, title: event.target.value })
                      }
                      className="mt-2 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-white outline-none"
                    />
                  </label>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted">
                    Status
                    <input
                      value={editing.status}
                      onChange={(event) =>
                        setEditing({ ...editing, status: event.target.value })
                      }
                      className="mt-2 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-white outline-none"
                    />
                  </label>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted sm:col-span-2">
                    Details
                    <textarea
                      value={editing.subtitle}
                      onChange={(event) =>
                        setEditing({ ...editing, subtitle: event.target.value })
                      }
                      className="mt-2 min-h-24 w-full rounded-xl border border-soft bg-white/[.04] px-4 py-3 text-white outline-none"
                    />
                  </label>
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => {
                    void save();
                  }}
                >
                  <Save size={16} />
                  Save record
                </Button>
                <button
                  onClick={() => setEditing(null)}
                  className="rounded-xl border border-soft px-4 py-3 text-sm font-bold"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
      <p className="mt-5 text-xs leading-5 text-muted">
        {apiMode
          ? "Connected to the protected Worker API. Changes are written to D1."
          : "Local demo edits are intentionally session-only. Start the API to manage D1 records."}
      </p>
    </div>
  );
}
